import {useEffect, useMemo, useRef, useState} from 'react'
import {Calendar, DatePicker} from '@material-ui/pickers'
import {format} from 'date-fns'
import {useTranslation} from 'react-i18next'

import CompactDateSelector from './CompactDateSelector'
import ImageCalendarDay from './ImageCalendarDay'
import PlanetHdPreparationStatus from './PlanetHdPreparationStatus'
import PlanetHdHistoricalProgress from './PlanetHdHistoricalProgress'
import {useViewImageContext} from './ViewImageContext'
import {useViewLandContext} from '../ViewLandContext'
import {useIsMobile} from '../../../../../../shared/responsive'
import {scrollElementIntoMobilePanel} from '../../../../../../shared/scrollIntoMobilePanel'
import {REMOTE_SENSING_DATE_STATES as STATES, canPrepareRemoteSensingDate} from './remoteSensingDateModel'
import {normalizeRemoteSensingSource, REMOTE_SENSING_PRODUCTS} from './remoteSensingModel'
import {useImageCloudinessFilter} from './useImageCloudinessFilter'
import {
  buildPlanetHdCalendarModel, getPlanetHdAdjacentDates, getPlanetHdDayCloudState, hasKnownCloudPercent,
  isPlanetHdNavigable, planetHdItemKey, selectPlanetHdDayItem,
} from './planetHdCalendarModel'
import {isPlanetHdDateAllowed, isPlanetHdCatalogFresh, PLANET_HD_CATALOG_TTL_MS} from './planetHdAcquisitionQuery'
import {getPlanetHdAccessIdentity} from './planetHdAccessIdentity'

const displayDate = (date) => format(new Date(`${date}T00:00:00`), 'dd/MM/yyyy')
const HD_PREPARATION_POLL_MS = 15000

const PlanetHdDateSelector = ({openSignal = 0, onOpenSignalHandled, onPreparingChange}) => {
  const {t} = useTranslation()
  const context = useViewImageContext()
  const {land} = useViewLandContext()
  const isMobile = useIsMobile()
  const activeImage = context.getActiveLandImage(land.land_id)
  const {changeRemoteSensingAcquisition, ensureLandHdAcquisitions, getLandHdAcquisitionsState, prepareLandHdAcquisition} = context
  const source = useMemo(() => normalizeRemoteSensingSource(activeImage.imageType), [activeImage.imageType])
  // In-flight preparation intents remain bound to the visualization that
  // initiated them; the selected acquisition/date lives in shared land state.
  const accessIdentity = getPlanetHdAccessIdentity(context.getHdAccessStatus?.(land.land_id)?.access)
  const scope = `${land.company_id || ''}:${land.land_id}:${activeImage.imageType}:${accessIdentity}`
  const scopeRef = useRef(scope)
  scopeRef.current = scope
  const {cloudinessFilter, setCloudinessFilter, debouncedCloudinessFilter, isUpdatingCloudiness} =
    useImageCloudinessFilter(land, REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD)
  const [inspection, setInspection] = useState(null)
  const [openedDatePicker, setOpenedDatePicker] = useState(false)
  const [openedMobileCalendar, setOpenedMobileCalendar] = useState(false)
  const [preparation, setPreparation] = useState(null)
  const [preparationError, setPreparationError] = useState(null)
  const [refreshingPreparation, setRefreshingPreparation] = useState(false)
  const refreshLock = useRef(null)
  // A preparation belongs to an acquisition, not to the calendar. Keep accepted
  // requests even if a catalog refresh fails, so returning to a date cannot
  // submit it twice. Clearing these refs never cancels a provider order.
  const preparationRequests = useRef(new Map())
  const pendingSelection = useRef(null)
  const lastHandledOpenSignal = useRef(0)
  const mobileCalendarRef = useRef(null)
  const refreshCatalogRef = useRef(null)
  // Null requests the same bounded catalog without a provider cloud threshold.
  // Cloud changes below are local: cloudy/unknown days must remain inspectable.
  const acquisitionsState = getLandHdAcquisitionsState(land, source, null)
  const items = useMemo(() => (acquisitionsState.items || []).filter(
    (item) => isPlanetHdDateAllowed(item?.date)
  ), [acquisitionsState.items])
  const {itemsByDate, availableDates, navigableDates} = useMemo(
    () => buildPlanetHdCalendarModel(items, debouncedCloudinessFilter), [items, debouncedCloudinessFilter]
  )
  const activeItem = activeImage.selectedAcquisitionId && items.find((item) => item.acquisitionId === activeImage.selectedAcquisitionId)
  const selectionDate = inspection?.scope === scope ? inspection.date : activeItem?.date ||
    (activeImage.selectedDate ? format(activeImage.selectedDate, 'yyyy-MM-dd') : null)
  const inspectedDate = selectionDate || availableDates[0]
  const dayItems = itemsByDate.get(inspectedDate) || []
  const selectedItem = selectPlanetHdDayItem(dayItems, inspection?.scope === scope ? inspection.id : activeImage.selectedAcquisitionId)
  const {previousDate, nextDate} = getPlanetHdAdjacentDates(navigableDates, selectionDate)
  const calendarDate = inspectedDate ? new Date(`${inspectedDate}T00:00:00`) : new Date()
  const isLoadingDates = acquisitionsState.status === 'loading' || isUpdatingCloudiness
  const hasAvailableDates = availableDates.length > 0
  const isCalendarOpen = isMobile ? openedMobileCalendar : openedDatePicker
  const pendingPreparation = preparation?.scope === scope && preparation
  const isPreparing = Boolean(pendingPreparation)

  useEffect(() => {
    onPreparingChange?.(isPreparing)
  }, [isPreparing, onPreparingChange])

  useEffect(() => () => onPreparingChange?.(false), [onPreparingChange])
  const pendingItem = pendingPreparation && items.find((item) => planetHdItemKey(item) === pendingPreparation.id)
  const hasPreparingItems = items.some((item) => item.status === STATES.PREPARING)

  refreshCatalogRef.current = () => {
    if (document.hidden || isPlanetHdCatalogFresh(getLandHdAcquisitionsState(land, source, null))) return
    // Metadata only: refreshing never selects an image or submits preparation.
    ensureLandHdAcquisitions(land, source, null).catch(() => {})
  }

  useEffect(() => {
    // Also refresh settled empty/error catalogs, whose date trigger may be
    // disabled. Only this visible HD view owns the timer, not the provider.
    const refresh = () => refreshCatalogRef.current?.()
    const timer = window.setInterval(refresh, PLANET_HD_CATALOG_TTL_MS)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [scope, acquisitionsState.fetchedAt])

  useEffect(() => {
    if (isCalendarOpen) refreshCatalogRef.current?.()
  }, [isCalendarOpen])

  useEffect(() => {
    // Only an authoritative terminal catalog state may release an accepted
    // request. A stale candidate after a failed refresh must keep its guard.
    items.forEach((item) => {
      const id = planetHdItemKey(item)
      if (preparationRequests.current.get(id)?.accepted &&
          ![STATES.CANDIDATE, STATES.PREPARING].includes(item.status)) {
        preparationRequests.current.delete(id)
      }
    })
  }, [items])

  useEffect(() => {
    ensureLandHdAcquisitions(land, source, null).catch(() => {})
  }, [ensureLandHdAcquisitions, land, source])

  useEffect(() => {
    if (acquisitionsState.status === 'error' || (!hasPreparingItems && !isPreparing)) return undefined
    const timer = window.setInterval(() => {
      ensureLandHdAcquisitions(land, source, null, {force: true}).catch(() => {})
    }, HD_PREPARATION_POLL_MS)
    return () => window.clearInterval(timer)
  }, [acquisitionsState.status, ensureLandHdAcquisitions, hasPreparingItems, isPreparing, land, source])

  useEffect(() => {
    if (!pendingItem || [STATES.CANDIDATE, STATES.PREPARING].includes(pendingItem.status)) return
    const intent = pendingSelection.current
    pendingSelection.current = null
    setPreparation(null)
    if (intent?.scope === scope && intent.id === planetHdItemKey(pendingItem) &&
        [STATES.READY, STATES.REJECTED_QUALITY].includes(pendingItem.status)) {
      setPreparationError(null)
      changeRemoteSensingAcquisition(land, pendingItem, source)
    }
  }, [pendingItem, scope, changeRemoteSensingAcquisition, land, source])

  useEffect(() => {
    setOpenedDatePicker(false)
    setOpenedMobileCalendar(false)
    setPreparationError(null)
    pendingSelection.current = null
    preparationRequests.current.clear()
    setPreparation(null)
    refreshLock.current = null
    setRefreshingPreparation(false)
  }, [scope])

  useEffect(() => () => {
    pendingSelection.current = null
    preparationRequests.current.clear()
    refreshLock.current = null
  }, [])

  useEffect(() => {
    if (openSignal <= lastHandledOpenSignal.current || context.loading || isLoadingDates || !hasAvailableDates) return
    lastHandledOpenSignal.current = openSignal
    onOpenSignalHandled?.(openSignal)
    if (isMobile) setOpenedMobileCalendar(true)
    else setOpenedDatePicker(true)
  }, [
    openSignal,
    context.loading,
    isLoadingDates,
    hasAvailableDates,
    isMobile,
    onOpenSignalHandled,
  ])

  useEffect(() => {
    if (!isMobile || !openedMobileCalendar) return undefined
    return scrollElementIntoMobilePanel(mobileCalendarRef.current, {delay: 160})
  }, [isMobile, openedMobileCalendar, availableDates.length])

  const inspectItem = (item) => {
    if (!item || !isPlanetHdDateAllowed(item.date) || context.loading || isLoadingDates) return
    setInspection({scope, date: item.date, id: planetHdItemKey(item)})
    setPreparationError(null)
    // Only an explicit date/arrow/alternative selection may request an image.
    // Opening the calendar, changing cloud threshold and polling never prepare.
    pendingSelection.current = null
    setPreparation(null)
    refreshLock.current = null
    setRefreshingPreparation(false)
    if ([STATES.READY, STATES.REJECTED_QUALITY].includes(item.status)) {
      changeRemoteSensingAcquisition(land, item, source)
    } else {
      // Never offer zoning on the previous image while a different date loads.
      context.changeDate(land, null)
      if ([STATES.CANDIDATE, STATES.PREPARING].includes(item.status)) {
        pendingSelection.current = {scope, id: planetHdItemKey(item)}
        if (canPrepareRemoteSensingDate(item)) prepareSelectedItem(item)
        else setPreparation({scope, id: planetHdItemKey(item), startedAt: null})
      }
    }
  }
  const selectDate = (date) => {
    const bucket = itemsByDate.get(format(date, 'yyyy-MM-dd'))
    inspectItem(getPlanetHdDayCloudState(bucket, debouncedCloudinessFilter).representative)
    setOpenedDatePicker(false)
    setOpenedMobileCalendar(false)
  }
  const navigateDate = (date) => {
    if (context.loading || isLoadingDates) return
    const bucket = itemsByDate.get(date)?.filter((item) => isPlanetHdNavigable(item, debouncedCloudinessFilter))
    inspectItem(selectPlanetHdDayItem(bucket, planetHdItemKey(selectedItem)))
  }
  const prepareSelectedItem = async (item) => {
    if (!canPrepareRemoteSensingDate(item)) return
    const id = planetHdItemKey(item)
    const existing = preparationRequests.current.get(id)
    if (existing) {
      setPreparation({scope, id, startedAt: existing.startedAt})
      return
    }
    const request = {startedAt: Date.now(), accepted: false}
    preparationRequests.current.set(id, request)
    const requestedScope = scope
    const isCurrentRequest = () => scopeRef.current === requestedScope &&
      preparationRequests.current.get(id) === request
    const isSelectedRequest = () => isCurrentRequest() &&
      pendingSelection.current?.scope === requestedScope && pendingSelection.current.id === id
    setPreparation({scope, id, startedAt: request.startedAt})
    setPreparationError(null)
    try {
      const result = await prepareLandHdAcquisition(land, item, source)
      if (!isCurrentRequest()) return
      if (!result) throw new Error('preparation_not_accepted')
      request.accepted = true
      const refreshed = await ensureLandHdAcquisitions(land, source, null, {force: true})
      if (refreshed?.status === 'error') throw new Error('preparation_refresh_failed')
    } catch (_) {
      if (isSelectedRequest()) {
        if (!request.accepted) {
          setPreparation(null)
          pendingSelection.current = null
        }
        setPreparationError(request.accepted
          ? t('images:hd_dates.load_refresh_error', {defaultValue: 'La imagen fue solicitada, pero no se pudo actualizar. Reintentá la consulta.'})
          : t('images:hd_dates.load_error', {defaultValue: 'No se pudo cargar la imagen. Volvé a elegir la fecha para reintentar.'}))
      }
    } finally {
      if (!request.accepted && preparationRequests.current.get(id) === request) {
        preparationRequests.current.delete(id)
      }
    }
  }
  const refreshPreparation = async () => {
    if (refreshLock.current) return
    const request = {}
    const requestedScope = scope
    const requestedSelection = pendingSelection.current
    refreshLock.current = request
    setRefreshingPreparation(true)
    try {
      const refreshed = await ensureLandHdAcquisitions(land, source, null, {force: true})
      if (refreshed?.status === 'error') throw new Error('preparation_refresh_failed')
      if (scopeRef.current === requestedScope && refreshLock.current === request &&
          pendingSelection.current === requestedSelection) setPreparationError(null)
    } catch (_) {
      if (scopeRef.current === requestedScope && refreshLock.current === request &&
          pendingSelection.current === requestedSelection) setPreparationError(
        t('images:hd_dates.load_refresh_error', {defaultValue: 'La imagen fue solicitada, pero no se pudo actualizar. Reintentá la consulta.'})
      )
    } finally {
      if (refreshLock.current === request) {
        refreshLock.current = null
        if (scopeRef.current === requestedScope) setRefreshingPreparation(false)
      }
    }
  }
  const cloudLabel = (item) => hasKnownCloudPercent(item)
    ? t('images:hd_dates.cloud_percent', {percent: item.cloudPercent.toFixed(1), defaultValue: `${item.cloudPercent.toFixed(1)}% nubes`})
    : t('images:hd_dates.unknown_cloud', {defaultValue: 'Nubosidad desconocida'})
  const cloudStatus = (item) => !hasKnownCloudPercent(item)
    ? cloudLabel(item)
    : t(item.cloudPercent <= debouncedCloudinessFilter ? 'images:date_status.clear' : 'images:date_status.cloudy')
  const renderCalendarDay = (day, pickerDate, inCurrentMonth, dayComponent) => {
    if (!inCurrentMonth) return dayComponent
    const date = format(day, 'yyyy-MM-dd')
    const bucket = itemsByDate.get(date) || []
    const {cloudState, representative: item} = getPlanetHdDayCloudState(bucket, debouncedCloudinessFilter)
    const description = item ? cloudLabel(item)
      : t('images:hd_dates.no_image', {defaultValue: 'Sin imagen'})
    return <ImageCalendarDay inCurrentMonth={inCurrentMonth} cloudState={item ? cloudState : null}
      active={date === inspectedDate} title={description} ariaLabel={`${displayDate(date)} · ${description}`}>
      {dayComponent}
    </ImageCalendarDay>
  }
  const shouldDisableDate = (date) => isLoadingDates || context.loading ||
    !itemsByDate.has(format(date, 'yyyy-MM-dd'))
  const triggerLabel = isLoadingDates ? t('images:dates_loading')
    : selectionDate ? `${displayDate(selectionDate)}${selectedItem ? ` · ${cloudStatus(selectedItem)}` : ''}` : t('images:date_selector')
  const statusMessage = acquisitionsState.status === 'empty' ? t('images:hd_dates.empty', {defaultValue: 'No hay adquisiciones Planet para este filtro.'})
      : acquisitionsState.status === 'error' ? items.length > 0
        ? t('images:hd_dates.refresh_error', {defaultValue: 'No se pudieron actualizar las adquisiciones HD. Se muestran las últimas fechas obtenidas.'})
        : t('images:hd_dates.error', {defaultValue: 'No se pudieron consultar las adquisiciones HD.'}) : null

  return <div className="view-land-hd-calendar">
    <PlanetHdHistoricalProgress landId={land.land_id}
      geometryHash={context.getHdAccessStatus?.(land.land_id)?.access?.geometry_hash}
      progress={context.getHdAccessStatus?.(land.land_id)?.access?.historical_preparation}
      refresh={context.ensureLandHdAccess}/>
    <CompactDateSelector canGoPrev={Boolean(previousDate) && !context.loading && !isLoadingDates}
      canGoNext={Boolean(nextDate) && !context.loading && !isLoadingDates}
      onPrev={() => navigateDate(previousDate)} onNext={() => navigateDate(nextDate)}
      onOpen={() => isMobile ? setOpenedMobileCalendar((open) => !open) : setOpenedDatePicker(true)}
      disabled={context.loading || isLoadingDates || !hasAvailableDates}
      isCalendarOpen={isCalendarOpen} isLoadingDates={isLoadingDates} triggerLabel={triggerLabel}
      cloudinessFilter={cloudinessFilter} onCloudinessChange={setCloudinessFilter} />
    {isMobile && openedMobileCalendar && hasAvailableDates && <div ref={mobileCalendarRef} className="view-land-date-selector__inline-calendar">
      <Calendar date={calendarDate} onChange={selectDate} renderDay={renderCalendarDay}
        shouldDisableDate={shouldDisableDate} allowKeyboardControl={false} />
    </div>}
    {!isMobile && <DatePicker open={openedDatePicker} value={calendarDate} autoOk okLabel={null}
      cancelLabel={t('common:actions.cancel')} renderDay={renderCalendarDay} shouldDisableDate={shouldDisableDate}
      onChange={selectDate} onOpen={() => setOpenedDatePicker(true)} onClose={() => setOpenedDatePicker(false)}
      animateYearScrolling DialogProps={{maxWidth: false, PaperProps: {className: 'view-land-date-picker-dialog'}}}
      TextFieldComponent={() => null} />}
    {statusMessage && <div className="view-land-hd-calendar__status" role={acquisitionsState.status === 'error' ? 'alert' : 'status'}>{statusMessage}</div>}
    {isPreparing && <PlanetHdPreparationStatus
      preparation={pendingItem?.preparation || pendingItem?.raw?.preparation}
      localStartedAt={pendingPreparation.startedAt}
      onRefresh={refreshPreparation}
      refreshing={refreshingPreparation || acquisitionsState.status === 'loading'} />}
    {acquisitionsState.status === 'error' && !isPreparing && <button type="button" className="btn btn-sm btn-light"
      onClick={() => ensureLandHdAcquisitions(land, source, null, {force: true}).catch(() => {})}>
      {t('images:hd_dates.retry', {defaultValue: 'Reintentar'})}
    </button>}
    {selectedItem && (preparationError ||
      [STATES.REJECTED_QUALITY, STATES.REJECTED, STATES.FAILED].includes(selectedItem.status)) && <div className="view-land-hd-calendar__detail">
      {selectedItem.status === STATES.REJECTED_QUALITY && <>
        <small role="alert">{selectedItem.qualityWarning || selectedItem.reason || t('images:hd_date_state.rejected_quality_warning', {defaultValue: 'Visualizable con advertencia; no apta para ambientar.'})}</small>
      </>}
      {[STATES.REJECTED, STATES.FAILED].includes(selectedItem.status) && <small role="alert">
        {t('images:hd_dates.image_unavailable', {defaultValue: 'No se pudo cargar esta imagen. Elegí otra fecha.'})}
      </small>}
      {preparationError && <small role="alert">{preparationError}</small>}
    </div>}
  </div>
}

export default PlanetHdDateSelector
